import { AGE_CLASSES, LINEAGES, TERRAINS } from "../data/lineages";
import type { Facet, GeneratorOptions } from "../types";
import { Icon } from "./Icon";

interface ControlsProps {
  options: GeneratorOptions;
  onOptionsChange: (options: GeneratorOptions) => void;
  onGenerate: () => void;
  onRandomize: () => void;
  onToggleLock: (facet: Facet) => void;
}

const LOCKS: Array<{ facet: Facet; label: string }> = [
  { facet: "identity", label: "Identity" },
  { facet: "rules", label: "Rules" },
  { facet: "lair", label: "Lair" },
  { facet: "hoard", label: "Hoard" },
  { facet: "factions", label: "Factions" },
  { facet: "history", label: "History" },
  { facet: "hooks", label: "Hooks" }
];

export function Controls({
  options,
  onOptionsChange,
  onGenerate,
  onRandomize,
  onToggleLock
}: ControlsProps) {
  const patch = <K extends keyof GeneratorOptions>(key: K, value: GeneratorOptions[K]) =>
    onOptionsChange({ ...options, [key]: value });

  return (
    <aside className="controls-panel ornate-panel" aria-label="Generator controls">
      <div className="panel-heading">
        <Icon name="dragon" className="panel-heading__mark" />
        Generator controls
      </div>
      <label className="control-field">
        <span>Species</span>
        <select value={options.lineage} onChange={(event) => patch("lineage", event.target.value)}>
          {LINEAGES.map((lineage) => (
            <option key={lineage.id} value={lineage.id}>
              {lineage.label}
            </option>
          ))}
        </select>
      </label>
      <label className="control-field">
        <span>Age</span>
        <select
          value={options.ageClass}
          onChange={(event) => patch("ageClass", event.target.value as GeneratorOptions["ageClass"])}
        >
          {AGE_CLASSES.map((age) => (
            <option key={age} value={age}>
              {age[0].toUpperCase()}
              {age.slice(1)}
            </option>
          ))}
        </select>
      </label>
      <label className="control-field">
        <span>Terrain</span>
        <select value={options.terrain} onChange={(event) => patch("terrain", event.target.value)}>
          {TERRAINS.map((terrain) => (
            <option key={terrain}>{terrain}</option>
          ))}
        </select>
      </label>
      <label className="control-field">
        <span>Tone</span>
        <select
          value={options.tone}
          onChange={(event) => patch("tone", event.target.value as GeneratorOptions["tone"])}
        >
          <option value="mythic">Mythic</option>
          <option value="grim">Grim</option>
          <option value="weird">Weird</option>
          <option value="classic">Classic</option>
          <option value="political">Political</option>
        </select>
      </label>
      <label className="control-field">
        <span>Danger level</span>
        <select
          value={options.danger}
          onChange={(event) => patch("danger", event.target.value as GeneratorOptions["danger"])}
        >
          <option value="low">Low</option>
          <option value="moderate">Moderate</option>
          <option value="high">High</option>
          <option value="mythic">Mythic</option>
        </select>
      </label>
      <label className="control-field">
        <span>Political influence</span>
        <select
          value={options.politics}
          onChange={(event) => patch("politics", event.target.value as GeneratorOptions["politics"])}
        >
          <option value="isolated">Isolated</option>
          <option value="local">Local</option>
          <option value="high">High</option>
          <option value="imperial">Imperial</option>
        </select>
      </label>
      <label className="control-field">
        <span>Rules adapter</span>
        <select
          value={options.adapter}
          onChange={(event) => patch("adapter", event.target.value as GeneratorOptions["adapter"])}
        >
          <option value="neutral">Neutral</option>
          <option value="srd51">SRD 5.1</option>
          <option value="srd52">SRD 5.2.1</option>
        </select>
      </label>

      <div className="lock-heading">Locked facets</div>
      <div className="lock-list">
        {LOCKS.map(({ facet, label }) => (
          <label className="lock-row" key={facet}>
            <input
              type="checkbox"
              checked={options.locks[facet]}
              onChange={() => onToggleLock(facet)}
            />
            <span className="lock-row__box" aria-hidden="true">
              {options.locks[facet] ? "✓" : ""}
            </span>
            <span>{label}</span>
            <span className="lock-row__icon" aria-hidden="true">
              <Icon name={options.locks[facet] ? "lock" : "spark"} />
            </span>
          </label>
        ))}
      </div>
      <div className="controls-panel__actions">
        <button type="button" className="generate-button" onClick={onGenerate}>
          <span>Generate dragon</span>
          <Icon name="dragon" className="generate-button__die" />
        </button>
        <button type="button" className="secondary-button" onClick={onRandomize}>
          <Icon name="reroll" /> Randomize all
        </button>
      </div>
    </aside>
  );
}
